import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Drawer, IconButton, Box, Tooltip } from "@mui/material";
import ListIcon from "@mui/icons-material/List";
import CloseIcon from "@mui/icons-material/Close";
import InsightsIcon from "@mui/icons-material/Insights";
import { trackActivity } from "../../../utils/mixpanel";
import { getCategoryIcon } from "./categoryMeta";
import "../styles/analytics.scss";

const ANALYTICS_CATEGORY_ORDER = [
    "Fleet Utilization Trends",
    "Revenue & Cashflow Performance",
    "Lease Expiry",
    "Insurance Expiry",
    "Credit Protection & Insurance Coverage",
    "Upcoming Escalation Events",
    "Active Alerts",
    "Maintenance Exposure",
    "Utilization Tracking",
    "Lessee Risk & Performance",
    "Compliance & Contractual Obligations",
    "Lease Options Pipeline",
];

function slugify(title) {
    return String(title || "")?.toLowerCase()?.replace(/&/g, "and")?.replace(/[^a-z0-9]+/g, "-")?.replace(/^-+|-+$/g, "");
}

function orderCategories(categories) {
    const byTitle = new Map(categories.map((c) => [c.title, c]));
    const ordered = [];
    for (const title of ANALYTICS_CATEGORY_ORDER) {
        if (byTitle.has(title)) {
            ordered.push(byTitle.get(title));
            byTitle.delete(title);
        }
    }
    for (const c of categories) {
        if (byTitle.has(c.title)) {
            ordered.push(c);
            byTitle.delete(c.title);
        }
    }
    return ordered;
}

const AnalyticsSidebar = ({ selectedGraph, setSelectedGraph, graphList= {} }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [graphArray, setGraphArray] = useState([]);
    const [open, setOpen] = useState(false);

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'Analytics', page_type: page_type })
    }

    const toggleDrawer = (newOpen) => () => {
        setOpen(newOpen);
    };

    useEffect(() => {
        const categories = orderCategories(
            Object.entries(graphList).map(([title, graphsData]) => ({
                title,
                slug: slugify(title),
                graphsData,
            })),
        );
        setGraphArray(categories);

        if (!categories.length) {
            setSelectedGraph({});
            return;
        }

        const paramCategory = searchParams.get("category");
        const matched = paramCategory
            ? categories.find((c) => c.slug === paramCategory)
            : null;

        setSelectedGraph((prev) => {
            if (!prev?.title) {
                return matched ?? categories[0];
            }
            const refreshed = categories.find((c) => c.title === prev.title);
            return refreshed ?? matched ?? categories[0];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [graphList]);

    useEffect(() => {
        if (selectedGraph?.slug && searchParams.get("category") !== selectedGraph.slug) {
            setSearchParams({ category: selectedGraph.slug }, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGraph]);

    const isActive = (item) => item?.title === selectedGraph?.title;

    const handleSelect = (item) => {
        setSelectedGraph(item);
        setSearchParams({ category: item.slug });
        handleItemClicked?.(`Category Selected: ${item.title}`);
    };

    return (
        <>
            {/* Mobile drawer */}
            <Box className="mobile-pry-container analytics-mobile-drawer-trigger" >
                <IconButton className="analytics-mobile-drawer-btn" onClick={toggleDrawer(true)}> <ListIcon /> </IconButton>
                <Drawer anchor="left" open={open} onClose={toggleDrawer(false)} PaperProps={{ className: "analytics-mobile-drawer-paper", }} >
                    <Box className="analytics-mobile-drawer-header">
                        <Box className="analytics-mobile-drawer-title">
                            <InsightsIcon className="analytics-mobile-drawer-title-icon" />
                            <span>Analytics</span>
                        </Box>
                        <IconButton size="small" className="analytics-mobile-drawer-close" onClick={toggleDrawer(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box className="analytics-mobile-drawer-body">
                        {graphArray?.length > 0 &&
                            graphArray.map((item) => {
                                const CategoryIcon = getCategoryIcon(item?.title);
                                return (
                                    <button key={item?.title} onClick={() => { handleSelect(item); setOpen(false); }} className={`analytics-mobile-nav-btn${isActive(item) ? " active" : ""}`} >
                                        <CategoryIcon className="analytics-mobile-nav-icon" />
                                        <span>{item?.title}</span>
                                    </button>
                                );
                            })}
                    </Box>
                </Drawer>
            </Box>

            {/* Desktop sidebar */}
            <div className="analytics-nav">
                <div className="analytics-nav-header">
                    <span className="analytics-nav-header-title">Analytics</span>
                    <span className="analytics-nav-header-subtitle">Fleet Insights</span>
                </div>
                <div className="analytics-nav-list">
                    {graphArray?.length > 0 &&
                        graphArray.map((item) => {
                            const CategoryIcon = getCategoryIcon(item?.title);
                            return (
                                <div className="analytics-nav-item" key={item?.title}>
                                    <Tooltip title={item?.title} enterDelay={50} enterNextDelay={50} placement="right" arrow slotProps={{ tooltip: { sx: { bgcolor: "white", color: "black", boxShadow: 2, border: "1px solid #ddd" }}, arrow: { sx: { color: "white" }}}}>
                                        <button className={`analytics-nav-btn${isActive(item) ? " active" : ""}`} onClick={() => handleSelect(item)}>
                                            <CategoryIcon className="analytics-nav-icon" />
                                            <span>{item?.title}</span>
                                        </button>
                                    </Tooltip>
                                </div>
                            );
                        })}
                </div>
            </div>
        </>
    );
};

export default AnalyticsSidebar;
